import React, { ChangeEvent } from "react";
import { AxiosResponse } from "axios";
import { AccountFormsField } from "./utils";
import { FaRegSave } from "react-icons/fa";
import { AddButton, ErrorHeader } from "../../Common/Components/FormComponents";
import styled from "styled-components";
import { ValidFieldIcon, InvalidFieldIcon } from "../../../components/utils/Icons/AccountsIcons";
import { passwordValidations } from "../helpers";

export interface AccountPasswordProps {
  modifyPassword: (password: string) => Promise<AxiosResponse<any, any>>;
}

const CustomSaveButton = styled(AddButton)`
  display: flex;
  gap: 0.5rem;
  flex-align: center;
  margin-top: 2rem;
  padding-left: 1.5rem;
  width: 15rem;
`;

const PasswordChangeVerification = styled(ErrorHeader)`
  color: ${({ theme }) => theme.colors.textDarkBlue};
`;

const PasswordSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: flex-start;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    flex-direction: row;
  }
`;

const FormContainer = styled.div`
  flex-grow: 1;
  width: 100%;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    max-width: 75%;
  }
`;

const PasswordRequirementsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    max-width: 25%;
    width: fit-content;
  }
`;

const ValidationTitle = styled.h3`
  margin: 1.25rem 0 1rem;
`;

const ValidationElement = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin: 0.25rem;

  svg {
    flex-shrink: 0;
  }
`;

const validationTexts = [
  { key: "shortLength", message: "Password is at least 8 characters" },
  { key: "longLength", message: "Password is less than 16 characters" },
  { key: "hasCap", message: "Password has at least one capital letter" },
  { key: "hasNum", message: "Password has at least one number" },
  { key: "hasSpecChar", message: "Password has at least one special character" },
  { key: "match", message: "Passwords match" },
];

const PasswordValidationElement: React.FC<{ isValid: boolean; validationText: string }> = props => (
  <ValidationElement data-testid="val-element">
    {props.isValid ? <ValidFieldIcon /> : <InvalidFieldIcon />}
    <div>{props.validationText}</div>
  </ValidationElement>
);

const PasswordRequirements: React.FC<{
  password: string;
  confirmPassword: string;
  setIsPasswordValid: any;
}> = props => {
  const { password, confirmPassword, setIsPasswordValid } = props;
  const validations = passwordValidations.map(vf => vf(password, confirmPassword));
  setIsPasswordValid(validations.every(Boolean));

  return (
    <PasswordRequirementsContainer>
      <ValidationTitle>Password Requirements</ValidationTitle>
      {validationTexts.map((vt, i: number) => (
        <PasswordValidationElement key={vt.key} isValid={validations[i]} validationText={vt.message} />
      ))}
    </PasswordRequirementsContainer>
  );
};

const AccountPassword: React.FC<AccountPasswordProps> = ({ modifyPassword }) => {
  const [passwordValue, setPasswordValue] = React.useState<string>("");
  const [confirmPasswordValue, setConfirmPasswordValue] = React.useState<string>("");
  const [displayVerification, setDisplayVerification] = React.useState<boolean>(false);
  const [editPassword, setEditPassword] = React.useState<boolean>(false);
  const [editConfirmPass, setEditConfirmPass] = React.useState<boolean>(false);
  const [isPasswordValid, setIsPasswordValid] = React.useState<boolean>(false);

  const handleOnClickPassword = () => setEditPassword(true);
  const handleOnClickConfirmPassword = () => setEditConfirmPass(true);

  const handleOnBlurPassword = () => setEditPassword(false);
  const handleOnBlurConfirmPassword = () => setEditConfirmPass(false);

  const handleOnChangePassword = (e: ChangeEvent<HTMLInputElement>) => setPasswordValue(e.target.value);

  const handleOnChangeConfirmPassword = (e: ChangeEvent<HTMLInputElement>) => setConfirmPasswordValue(e.target.value);

  const handleSavePasswordClick = () => {
    modifyPassword(passwordValue);
    setDisplayVerification(true);
  };

  const closeOpenText = () => null;

  return (
    <PasswordSectionContainer>
      <FormContainer>
        <AccountFormsField
          edit={editPassword}
          legend="New password"
          type="password"
          name="new-password"
          aria-label="new-password"
          closeOpenText={closeOpenText}
          errorMessage={null}
          displayVerification={false}
          value={passwordValue}
          onChange={handleOnChangePassword}
          onClickInput={handleOnClickPassword}
          onBlur={handleOnBlurPassword}
        />
        <AccountFormsField
          edit={editConfirmPass}
          legend="Confirm password"
          type="password"
          name="confirm-password"
          aria-label="confirm-password"
          closeOpenText={closeOpenText}
          errorMessage={null}
          displayVerification={false}
          value={confirmPasswordValue}
          onChange={handleOnChangeConfirmPassword}
          onClickInput={handleOnClickConfirmPassword}
          onBlur={handleOnBlurConfirmPassword}
        />
        {displayVerification && <PasswordChangeVerification>Your password was changed</PasswordChangeVerification>}
        <br />
        <CustomSaveButton onClick={handleSavePasswordClick} disabled={passwordValue === "" || !isPasswordValid}>
          <FaRegSave />
          Save Password
        </CustomSaveButton>
      </FormContainer>
      <PasswordRequirements
        password={passwordValue}
        confirmPassword={confirmPasswordValue}
        setIsPasswordValid={setIsPasswordValid}
      />
    </PasswordSectionContainer>
  );
};

export default AccountPassword;
